import { inject, Injectable } from "@angular/core";

import { LogService } from "@bitwarden/common/abstractions/log.service";
import { CreditConfigType } from "@bitwarden/web-vault/app/models/types/environement-config.type";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { capFetch } from "@bitwarden/web-vault/utils/fetch";
import { ITransactionResponse } from "@bitwarden/web-vault/app/models/interfaces/credits.interfaces";
import {
  PluginConfigService,
  PluginIdentifier,
} from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { GLOSS_CREDITS_CATEGORY_ID } from "@bitwarden/web-vault/app/models/constants/category.constants";
import { CreditsService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/credits.service";
import { GlossCreditsAccountService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/gloss-credits-account.service";
import { mapGlossCreditsTransactionToStoreModel } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/gloss-credits-transaction.mapper";

const CREDIT_CONFIG = process.env.CREDIT as unknown as CreditConfigType;

@Injectable({
  providedIn: "root",
})
export class GlossCreditsTransactionSyncService {
  private log: LogService = inject(LogService);
  private pluginConfigService: PluginConfigService = inject(PluginConfigService);
  private creditsService: CreditsService = inject(CreditsService);
  private glossCreditsAccountService: GlossCreditsAccountService = inject(
    GlossCreditsAccountService,
  );
  private transactionStore: TransactionStoreService = inject(TransactionStoreService);

  /**
   * Syncs Gloss Credits transactions from the credit API to the vault.
   * Fetches transactions, maps to TransactionStoreModel, saves to vault,
   * and ensures reference data exists for GLOSS_CREDITS_SYMBOL/AUD.
   */
  async syncGlossCreditsTransactions(): Promise<void> {
    try {
      const account = this.glossCreditsAccountService.findGlossCreditsAccount();
      if (!account) {
        this.log.info(
          "GlossCreditsTransactionSyncService: No Gloss Credits account, skipping sync",
        );
        return;
      }

      // A tier-2 init failure leaves transactionViews() null. Without this guard the
      // existence diff below sees zero existing rows and re-writes every credits
      // transaction as a new cipher (duplicate vault records). Bail before any write.
      if (!this.transactionStore.transaction.transactionViews()) {
        this.log.info(
          "GlossCreditsTransactionSyncService: Transaction store not initialised, skipping sync",
        );
        return;
      }

      const config = this.pluginConfigService.getConfig(PluginIdentifier.CREDIT);
      if (!config?.apiKey) {
        this.log.info("GlossCreditsTransactionSyncService: No credit API key, skipping sync");
        return;
      }

      const apiTransactions = await this.fetchTransactionsFromApi(config.apiKey);
      const existingVids = this.getExistingGlossCreditsVids(account.id);
      const staleIds = this.getStaleGlossCreditsTransactionIds(account.id);
      const transactionsToSave = apiTransactions.filter((t) => {
        const vaultId = `gloss-credits-${t._id}`;
        return !existingVids.has(vaultId) || staleIds.has(vaultId);
      });

      if (transactionsToSave.length > 0) {
        const transactionViews = transactionsToSave.map((t) => {
          const storeModel = mapGlossCreditsTransactionToStoreModel(t, account);
          storeModel.vid = existingVids.get(storeModel.id) ?? "";
          return new TransactionView(storeModel);
        });

        await this.transactionStore.transaction.saveToVault(transactionViews, true);
        await this.creditsService.saveGlossCreditsReferences();
        await this.creditsService.fetch();
        this.log.info(
          `GlossCreditsTransactionSyncService: Synced ${transactionsToSave.length} transactions (${staleIds.size} repaired)`,
        );
      } else {
        // No writes happened, so nothing to refresh — skip the full fetch() chain
        // (authenticate + link + fetchUserAccounts + fetchCredits) to avoid wasted
        // network I/O on every steady-state no-op sync.
        this.log.info("GlossCreditsTransactionSyncService: No new transactions to sync");
      }
    } catch (error) {
      this.log.error(`GlossCreditsTransactionSyncService: Sync failed: ${error?.message || error}`);
    }
  }

  private async fetchTransactionsFromApi(apiKey: string): Promise<ITransactionResponse[]> {
    const baseUrl = this.creditsService.baseUrl;
    const url = `${baseUrl}${CREDIT_CONFIG.endpoint.transactions}`;

    const response = await capFetch<{ transactions: ITransactionResponse[] }>({
      url,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
    });

    if (response.status !== 200) {
      throw new Error(`Credit API returned ${response.status}`);
    }

    return response.data?.transactions ?? [];
  }

  private getExistingGlossCreditsVids(accountId: string): Map<string, string> {
    // Non-null past the syncGlossCreditsTransactions() guard (the only caller).
    const transactions = this.transactionStore.transaction.transactionViews();
    return new Map(
      transactions
        .filter((t) => t.accountLink?.id === accountId && t.id?.startsWith("gloss-credits-"))
        .map((t) => [t.id, t.toStoreModel().vid]),
    );
  }

  private getStaleGlossCreditsTransactionIds(accountId: string): Set<string> {
    // Non-null past the syncGlossCreditsTransactions() guard (the only caller).
    const transactions = this.transactionStore.transaction.transactionViews();

    return new Set(
      transactions
        .filter(
          (t) =>
            t.accountLink?.id === accountId &&
            t.id?.startsWith("gloss-credits-") &&
            (!t.quantity?.convsym ||
              t.allocations?.[0]?.categoryLink?.id !== GLOSS_CREDITS_CATEGORY_ID),
        )
        .map((t) => t.id),
    );
  }
}
